/**
 * Country Filters Component - Filter sidebar for countries
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Label } from '@voilajsx/uikit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { Checkbox } from '@voilajsx/uikit/checkbox';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { CountryFilterOptions, CountryFilters } from '../types';

interface CountryFiltersProps {
  filters: CountryFilters;
  filterOptions: CountryFilterOptions;
  onFilterChange: (filters: CountryFilters) => void;
  onReset: () => void;
}

export const CountryFilters: React.FC<CountryFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onReset
}) => {
  const [localFilters, setLocalFilters] = useState<CountryFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    onFilterChange(localFilters);
  };

  const hasActiveFilters =
    localFilters.region ||
    localFilters.minSafetyIndex !== undefined ||
    localFilters.maxLivingCost !== undefined ||
    localFilters.postStudyWork ||
    localFilters.partTimeWork ||
    localFilters.permanentResidency;

  return (
    <div className="bg-card rounded-3xl p-7 shadow-sm border border-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
            <SlidersHorizontal className="h-5 w-5 text-success" />
          </div>
          <h2 className="text-xl font-bold text-foreground m-0">
            Filters
          </h2>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-muted-foreground px-3 py-1.5 text-sm font-semibold"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {/* Region */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Region
          </Label>
          <Select
            value={localFilters.region || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, region: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="All regions" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">All Regions</SelectItem>
              {filterOptions.regions.map((region) => (
                <SelectItem key={region} value={region} className="text-[15px]">
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Safety Index */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Minimum Safety Index
          </Label>
          <Select
            value={localFilters.minSafetyIndex?.toString() || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, minSafetyIndex: value === 'all' ? undefined : parseFloat(value) })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="Any safety level" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">Any Safety Level</SelectItem>
              <SelectItem value="60" className="text-[15px]">60+ (Good)</SelectItem>
              <SelectItem value="70" className="text-[15px]">70+ (Very Good)</SelectItem>
              <SelectItem value="80" className="text-[15px]">80+ (Excellent)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Max Living Cost */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Max Living Cost/Year
          </Label>
          <Select
            value={localFilters.maxLivingCost?.toString() || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, maxLivingCost: value === 'all' ? undefined : parseInt(value) })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="Any budget" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">Any Budget</SelectItem>
              <SelectItem value="10000" className="text-[15px]">Under $10k</SelectItem>
              <SelectItem value="15000" className="text-[15px]">Under $15k</SelectItem>
              <SelectItem value="20000" className="text-[15px]">Under $20k</SelectItem>
              <SelectItem value="30000" className="text-[15px]">Under $30k</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Work & Immigration Filters */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 block">
            Work & Immigration
          </Label>
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <Checkbox
                id="part-time-work"
                checked={localFilters.partTimeWork || false}
                onCheckedChange={(checked) =>
                  setLocalFilters({ ...localFilters, partTimeWork: checked as boolean })
                }
                className="border-border data-[state=checked]:bg-success data-[state=checked]:border-success"
              />
              <Label
                htmlFor="part-time-work"
                className="text-[15px] font-medium text-foreground cursor-pointer"
              >
                Part-time Work Allowed
              </Label>
            </div>

            <div className="flex items-center gap-2.5">
              <Checkbox
                id="post-study-work"
                checked={localFilters.postStudyWork || false}
                onCheckedChange={(checked) =>
                  setLocalFilters({ ...localFilters, postStudyWork: checked as boolean })
                }
                className="border-border data-[state=checked]:bg-success data-[state=checked]:border-success"
              />
              <Label
                htmlFor="post-study-work"
                className="text-[15px] font-medium text-foreground cursor-pointer"
              >
                Post-Study Work Visa
              </Label>
            </div>

            <div className="flex items-center gap-2.5">
              <Checkbox
                id="permanent-residency"
                checked={localFilters.permanentResidency || false}
                onCheckedChange={(checked) =>
                  setLocalFilters({ ...localFilters, permanentResidency: checked as boolean })
                }
                className="border-border data-[state=checked]:bg-success data-[state=checked]:border-success"
              />
              <Label
                htmlFor="permanent-residency"
                className="text-[15px] font-medium text-foreground cursor-pointer"
              >
                PR Pathway Available
              </Label>
            </div>
          </div>
        </div>

        {/* Sort By */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Sort By
          </Label>
          <Select
            value={localFilters.sort || 'name'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, sort: value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="name" className="text-[15px]">Country Name</SelectItem>
              <SelectItem value="safety" className="text-[15px]">Safety Index</SelectItem>
              <SelectItem value="cost" className="text-[15px]">Living Cost</SelectItem>
              <SelectItem value="universities" className="text-[15px]">Number of Universities</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Apply Button */}
      <Button
        onClick={handleApply}
        className="w-full bg-success text-primary-foreground hover:bg-success/90 px-3.5 py-3.5 rounded-xl font-semibold text-[15px] mt-7"
      >
        Apply Filters
      </Button>
    </div>
  );
};
